import { Injectable, EventEmitter } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { PlatformName, PlatformProvider } from '@shared';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { Position } from './position.model';
import { WorkspaceDatabaseKeys } from '@database/workspace';
import { StorageService, AlertService } from '@shared';
import { ToastService, ToastDuration } from '@shared';

@Injectable()
export class GeolocationService {
    private GPS_STATUS_KEY = 'GPS_STATUS';
    public gpsStatusChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

    public constructor(
        private platformProvider: PlatformProvider,
        private storageService: StorageService,
        private toastService: ToastService,
        private alertService: AlertService
    ) {}

    public async getCurrentPosition(): Promise<Position> {
        if (this.platformProvider.getPlatform() !== PlatformName.WEB) {
            await Geolocation.requestPermissions();
        }

        const position = await Geolocation.getCurrentPosition();
        return position.coords;
    }

    public async getCurrentPositionWithoutPermissionCheck(): Promise<Position> {
        try {
            const position = await Geolocation.getCurrentPosition();
            return position.coords;
        } catch (error) {
            await this.presentLocationSettingAlert();
            this.toastService.presentError('Error retrieving location. Please enable Location service on device.', ToastDuration.Fast);
        }
    }

    public async setGPSValue(gpsToggleEventValue: boolean): Promise<void> {
        const platform = this.platformProvider.getPlatform();
        if (platform === PlatformName.WEB) {
            await this.storageService.set(this.GPS_STATUS_KEY, false);
            this.gpsStatusChanged.emit(false);
            if (gpsToggleEventValue) {
                this.toastService.presentError('GPS cannot be enabled on the web platform.', ToastDuration.Fast);
            }
        } else {
            if (gpsToggleEventValue) {
                try {
                    const permission = await Geolocation.requestPermissions();
                    if (permission.location === 'granted') {
                        await this.storageService.set(this.GPS_STATUS_KEY, true);
                        this.gpsStatusChanged.emit(true);
                    } else {
                        await this.storageService.set(this.GPS_STATUS_KEY, false);
                        this.gpsStatusChanged.emit(false);
                        await this.presentPermissionDeniedAlert();
                    }
                } catch (error) {
                    await this.storageService.set(this.GPS_STATUS_KEY, false);
                    this.toastService.presentError('Error enabling GPS. Please try again.', ToastDuration.Fast);
                    await this.presentLocationSettingAlert();
                }
            } else {
                await this.storageService.set(this.GPS_STATUS_KEY, false);
                this.gpsStatusChanged.emit(false);
            }
        }
    }

    public mapWorkfilePropertyToPositionProperty(property: string): string | undefined {
        switch (property) {
            case WorkspaceDatabaseKeys.columns.elevation:
                return 'altitude';
            case WorkspaceDatabaseKeys.columns.northSouthDecimalColumn:
                return 'latitude';
            case WorkspaceDatabaseKeys.columns.eastWestDecimalColumn:
                return 'longitude';
            default:
                return undefined;
        }
    }

    private async openSettings(option: 'app' | 'location'): Promise<void> {
        try {
            if (this.platformProvider.getPlatform() === PlatformName.IOS) {
                await NativeSettings.openIOS({
                    option: option === 'app' ? IOSSettings.App : IOSSettings.LocationServices,
                });
            } else if (this.platformProvider.getPlatform() === PlatformName.ANDROID) {
                await NativeSettings.openAndroid({
                    option: option === 'app' ? AndroidSettings.ApplicationDetails : AndroidSettings.Location,
                });
            }
        } catch (error) {
            this.toastService.presentError('Failed to open settings. Please try manually.', ToastDuration.Fast);
        }
    }

    private async presentAlert(message: string, buttonText: string, settingType: 'app' | 'location'): Promise<void> {
        const buttons = [
            {
                text: 'Close',
                role: 'cancel'
            },
            {
                text: buttonText,
                handler: () => {
                    this.openSettings(settingType);
                }
            }
        ];

        await this.alertService.presentAlert(
            message,
            buttons,
            true
        );
    }

    private async presentPermissionDeniedAlert(): Promise<void> {
        await this.presentAlert(
            'GPS permission is required for this app to function correctly. Please enable GPS in your app settings to continue.',
            'Open Settings',
            'app'
        );
    }

    private async presentLocationSettingAlert(): Promise<void> {
        await this.presentAlert(
            'Location permission is required to enable this feature. Please enable your Location in your settings to continue.',
            'Open Location Settings',
            'location'
        );
    }

    public async getCurrentGPSStatus(): Promise<boolean> {
        const status = await this.storageService.get(this.GPS_STATUS_KEY);
        return !!status;
    }
}
